import React, { useRef, useState } from 'react';
import { Button, Copy, Column, Row } from './components';

function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Read failed'));
    reader.readAsDataURL(file);
  });
}

export function LogoInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  async function choose(file?: File) {
    setError('');
    if (!file) return;
    const supported =
      ['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      (!file.type && /\.(png|jpe?g|webp)$/i.test(file.name));
    if (!supported || file.size > 8 * 1024 * 1024) {
      setError('Choose a PNG, JPEG or WebP image smaller than 8 MB.');
      return;
    }
    setLoading(true);
    try {
      const image = new Image();
      // Data URLs work with the production img-src policy; blob URLs do not.
      image.src = await readImage(file);
      await image.decode();
      const canvas = document.createElement('canvas');
      for (const size of [128, 96, 72, 54]) {
        const scale = Math.min(1, size / Math.max(image.width, image.height));
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Image processing unavailable');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const logo = canvas.toDataURL('image/png');
        if (logo.length <= 24000) {
          onChange(logo);
          return;
        }
      }
      throw new Error('Image processing failed');
    } catch {
      setError(
        'We couldn’t open this image. Please try selecting it again or export it as a new PNG.',
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Column gap={8}>
      <Copy bold>{label}</Copy>
      <Row wrap>
        {!!value && (
          <img
            src={value}
            alt="Selected venture logo"
            style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 12 }}
          />
        )}
        <Button kind="secondary" disabled={loading} onPress={() => input.current?.click()}>
          {loading ? 'Preparing logo…' : value ? 'Change logo' : 'Upload logo'}
        </Button>
        {!!value && (
          <Button
            kind="quiet"
            disabled={loading}
            onPress={() => {
              setError('');
              onChange('');
            }}
          >
            Remove logo
          </Button>
        )}
      </Row>
      <Copy muted size={12}>
        PNG, JPEG or WebP · up to 8 MB. We resize it for you.
      </Copy>
      <input
        ref={input}
        aria-label={label}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        disabled={loading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          void choose(file);
        }}
        style={{ display: 'none' }}
      />
      {!!error && (
        <div
          role="alert"
          style={{ color: '#a83d2d', padding: 8, border: '1px solid #a83d2d', borderRadius: 8 }}
        >
          {error}
        </div>
      )}
    </Column>
  );
}
